import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { messageLogsQuery } from 'components/domain/automation/messageLogs/messageLogsQuery';
import useFilterFormContext from 'components/filters/useFilterFormContext';
import CommonTable from 'components/table/CommonTable';
import FormattedDateCell from 'components/table/cells/common/FormattedDateCell';
import useServerFilteredTable from 'components/table/hooks/useServerFilteredTable';
import { MessageLog } from 'models/automation/MessageLogs';
import FormattedDateWithTimezone from 'pages/automations/common/cells/FormattedDateWithTimezone';
import TriggerActionTypeCell from 'pages/automations/common/cells/TriggerActionTypeCell';
import LeadCell from 'pages/automations/common/cells/lead/LeadCell';
import PropertyCell from 'pages/automations/common/cells/property/PropertyCell';
import { MessageLogTabFiltersSchemaType } from '../filters/MessageLogTabFilters.schema';
import useSearchedMessageLog from '../filters/useSearchedMessageLog';
import ActionsCell from './cells/ActionsCell';
import LogTypeCell from './cells/LogTypeCell';

const MessageLogTable = () => {
  const { t } = useTranslation();
  const searchTerm = useSearchedMessageLog();
  const { lastSubmittedFilters } =
    useFilterFormContext<MessageLogTabFiltersSchemaType>();
  const columnHelper = createColumnHelper<MessageLog>();

  const columns = [
    columnHelper.accessor('executionTime', {
      header: t('pageAutomations.messageLog.table.executionTime'),
      cell: FormattedDateCell,
      sortDescFirst: true,
      meta: {
        dateFormat: 'PPp',
        cellTooltipText: FormattedDateWithTimezone,
      },
    }),
    columnHelper.accessor('messageName', {
      header: t('pageAutomations.messageLog.table.messageName'),
      sortDescFirst: true,
    }),
    columnHelper.accessor('action', {
      header: t('pageAutomations.messageLog.table.action'),
      cell: TriggerActionTypeCell,
      enableSorting: false,
    }),
    columnHelper.accessor('leadUid', {
      header: t('pageAutomations.messageLog.table.leadUid'),
      cell: LeadCell,
      enableSorting: false,
      meta: {
        showCellTooltip: false,
      },
    }),
    columnHelper.accessor('propertyUid', {
      header: t('pageAutomations.scheduledMessages.table.propertyUid'),
      cell: PropertyCell,
      enableSorting: false,
    }),
    columnHelper.accessor('logType', {
      header: t('pageAutomations.messageLog.table.logType'),
      cell: LogTypeCell,
      enableSorting: false,
      meta: {
        showCellTooltip: false,
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common.actions'),
      cell: ActionsCell,
      meta: {
        showCellTooltip: false,
      },
    }),
  ];

  const { tableInstance, isLoading } = useServerFilteredTable({
    columns,
    getQuery: (options) =>
      messageLogsQuery({
        filters: { ...lastSubmittedFilters },
        searchTerm,
        ...options,
      }),
    pageSize: 10,
  });

  return (
    <CommonTable
      data-testid="message-logs-table"
      emptyState={t('pageAutomations.messageLog.emptyTable')}
      isLoading={isLoading}
      tableInstance={tableInstance}
    />
  );
};

export default MessageLogTable;
